import type { InvoiceIssuerInput, InvoiceNumberingInput, InvoiceSettingsDto } from "@tc/shared";

/**
 * Back-office org-wide invoicing settings (TW-3527) — the factura issuer block + the numbering
 * scheme, edited in Settings → Invoicing. Back-office only (the section is gated on `invoice.settings`
 * + BACK_OFFICE in SettingsPage; the API re-checks). Same-origin, so the session cookie rides along.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "/api";

/** Thrown on a failed settings call so the form can surface the right inline state. */
export class InvoiceSettingsError extends Error {
  constructor(readonly status: number) {
    super(`Invoice settings request failed (${status})`);
  }
}

/** GET /api/invoice-settings — the org settings (empty defaults when never configured). */
export async function fetchInvoiceSettings(): Promise<InvoiceSettingsDto> {
  const res = await fetch(`${API_URL}/invoice-settings`, { headers: { accept: "application/json" } });
  if (!res.ok) throw new InvoiceSettingsError(res.status);
  return (await res.json()) as InvoiceSettingsDto;
}

/** PUT /api/invoice-settings/issuer — save the issuer block; returns the full settings. */
export async function saveInvoiceIssuer(input: InvoiceIssuerInput): Promise<InvoiceSettingsDto> {
  const res = await fetch(`${API_URL}/invoice-settings/issuer`, {
    method: "PUT",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new InvoiceSettingsError(res.status);
  return (await res.json()) as InvoiceSettingsDto;
}

/** PUT /api/invoice-settings/numbering — save the numbering scheme; returns the full settings. */
export async function saveInvoiceNumbering(input: InvoiceNumberingInput): Promise<InvoiceSettingsDto> {
  const res = await fetch(`${API_URL}/invoice-settings/numbering`, {
    method: "PUT",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new InvoiceSettingsError(res.status);
  return (await res.json()) as InvoiceSettingsDto;
}
